import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { AlertTriangle, Printer, Receipt } from 'lucide-react';
import ChefHeader from '../shared/ui/ChefHeader';
import { Card, EmptyState, LoadingView } from '../shared/ui';
import { generateReceiptQR, listenToOrder } from '../shared/services/orderService';

// ---------- Receipt printing ----------
// Compact 80mm-thermal layout (72mm printable width) — the on-screen page
// stays iOS-style; this document is what actually feeds the printer.

const escHtml = (s) =>
  String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// "Customer Chef" -> "C Chef": first initial + last name, so the full name
// isn't handed to drivers but staff can still call it out.
function shortCustomerName(fullName) {
  const parts = String(fullName || 'Customer').trim().split(/\s+/);
  if (parts.length < 2) return parts[0];
  return `${parts[0][0].toUpperCase()}. ${parts.slice(1).join(' ')}`;
}

function receiptHtml(order, qrDataUrl) {
  const items = (order.orderItems || [])
    .map((it) => {
      const name = (it.menuItem?.name || it.menuItemName || it.name || 'Item') + (it.variantName ? ` (Size: ${it.variantName})` : '');
      const qty = it.quantity || 1;
      return `<tr><td class="qty">${qty}&times;</td><td>${escHtml(name)}</td></tr>`;
    })
    .join('');
  const addr = order.deliveryAddress?.street || order.customerAddress || '';
  return `<!doctype html><html><head><meta charset="utf-8"><style>
@page { size: 80mm auto; margin: 4mm; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { width: 72mm; margin: 0 auto; font-family: 'Courier New', monospace; font-size: 13px; font-weight: bold; color: #000; padding: 2mm 0; }
.c { text-align: center; }
.b { font-weight: bold; }
.ordernum { font-size: 16px; font-weight: 900; word-break: break-all; }
.status { font-size: 12px; font-weight: bold; margin-top: 2mm; }
hr { border: none; border-top: 1px dashed #000; margin: 3mm 0; }
table { width: 100%; border-collapse: collapse; }
td { padding: 0.5mm 0; vertical-align: top; }
td.qty { width: 8mm; }
td.r { text-align: right; white-space: nowrap; }
.row { display: flex; justify-content: space-between; gap: 3mm; margin: 0.5mm 0; }
.row span:last-child { text-align: right; word-break: break-word; }
img.qr { width: 46mm; height: 46mm; display: block; margin: 2mm auto; }
.small { font-size: 10px; font-weight: bold; }
</style></head><body>
<div class="c b" style="font-size:14px">CheF SKN</div>
<div class="c b" style="font-size:18px; margin-top:1mm">${escHtml(order.restaurantName)}</div>
<div class="c">ORDER RECEIPT</div>
<hr>
<div class="c small">ORDER #</div>
<div class="c ordernum">${escHtml(order.orderNumber)}</div>
<div class="c status">READY FOR PICKUP</div>
<div class="c b" style="font-size:22px; margin-top:2mm">${escHtml(shortCustomerName(order.customerName))}</div>
<hr>
<table>${items}</table>
<hr>
<div class="row"><span>Phone</span><span>${escHtml(order.customerPhone || 'Not provided')}</span></div>
${addr ? `<div class="row"><span>Address</span><span>${escHtml(addr)}</span></div>` : ''}
<div class="row"><span>Items</span><span>${(order.orderItems || []).length} item(s)</span></div>
<hr>
<div class="c b">SCAN TO CONFIRM PICKUP</div>
${qrDataUrl ? `<img class="qr" src="${qrDataUrl}">` : '<div class="c">[QR unavailable]</div>'}
<div class="c small">Attach this receipt to the order bag</div>
<div class="c small">Driver will scan QR code upon arrival</div>
</body></html>`;
}

// Native: cordova-plugin-printer → Android PrintManager (thermal print
// services). Web: a dedicated print window so only the receipt prints —
// never the surrounding app chrome.
function printReceipt(order, qrDataUrl) {
  const html = receiptHtml(order, qrDataUrl);
  const printer = window.cordova?.plugins?.printer;
  if (Capacitor.isNativePlatform() && printer) {
    printer.print(html, { name: `Receipt ${order.orderNumber || ''}` });
    return;
  }
  const w = window.open('', '_blank', 'width=420,height=640');
  if (!w) {
    window.print();
    return;
  }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-gray-500 font-bold shrink-0">{label}</span>
      <span className="text-gray-900 font-bold text-right break-words">{value}</span>
    </div>
  );
}

function InstructionRow({ number, text }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center shrink-0">
        {number}
      </span>
      <p className="text-sm text-gray-800">{text}</p>
    </div>
  );
}

// Mirrors ReceiptView.swift: generates the receipt QR on first open (writes
// qrCodeData/qrGeneratedAt/qrCodeActive), shows a pulsing GREEN ring while
// waiting for the driver scan, and switches to a RED state once the live
// listener sees qrScannedAt set on the order doc.
export default function OrderReceiptPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrBusy, setQrBusy] = useState(false);
  const [qrError, setQrError] = useState('');
  const generatedRef = useRef(false);

  // Live listener — watches qrScannedAt for the green → red transition.
  useEffect(() => {
    if (!orderId) return undefined;
    setLoading(true);
    return listenToOrder(orderId,
      (o) => { setOrder(o); setLoading(false); setError(''); },
      (e) => { setError(e.message || 'Failed to load order.'); setLoading(false); });
  }, [orderId]);

  // Generate once on first open (guarded against StrictMode double-effect).
  useEffect(() => {
    if (!order || generatedRef.current) return;
    generatedRef.current = true;
    setQrBusy(true);
    generateReceiptQR(order)
      .then(({ dataUrl }) => setQrDataUrl(dataUrl))
      .catch((e) => setQrError(e.message || 'Failed to generate QR code'))
      .finally(() => setQrBusy(false));
  }, [order]);

  const scanned = !!order?.qrScannedAt;

  return (
    <div>
      <ChefHeader
        title="Receipt"
        right={(
          <button
            onClick={() => navigate(`/business/orders/${orderId}`)}
            className="text-sm font-semibold text-[#FF6B35] px-2"
          >
            Done
          </button>
        )}
      />

      {loading ? (
        <LoadingView label="Loading receipt…" />
      ) : error && !order ? (
        <EmptyState icon={AlertTriangle} title="Couldn't load order" subtitle={error} />
      ) : !order ? (
        <EmptyState icon={Receipt} title="Order not found" subtitle="It may have been deleted." />
      ) : (
        <div className="p-4 space-y-6">
          <div className="text-center space-y-1">
            <p className="text-xl font-black text-gray-900 break-words">{order.restaurantName}</p>
            <h1 className="text-2xl font-extrabold text-gray-900">Order Receipt</h1>
          </div>

          {/* Order number + pickup state */}
          <div className="text-center py-2 space-y-2">
            <p className="text-lg text-gray-400">Order #</p>
            <p className="text-5xl font-black text-gray-900 leading-none break-words">
              {order.orderNumber}
            </p>
            <p className={`text-lg font-semibold ${scanned ? 'text-red-500' : 'text-green-600'}`}>
              {scanned ? 'Picked Up by Driver' : 'Ready for Pickup'}
            </p>
            <p className="text-4xl font-black text-gray-900 pt-1 break-words">{shortCustomerName(order.customerName)}</p>
          </div>

          {/* QR code */}
          <div className="flex flex-col items-center gap-4">
            <p className={`text-sm font-bold ${scanned ? 'text-green-600' : 'text-gray-500'}`}>
              {scanned ? 'Order Picked Up ✓' : 'Scan to Confirm Pickup'}
            </p>

            {qrBusy ? (
              <LoadingView label="Generating QR Code…" />
            ) : qrDataUrl ? (
              <div className="relative">
                <img
                  src={qrDataUrl}
                  alt={`QR code for order ${order.orderNumber}`}
                  className="w-[200px] h-[200px] rounded-xl bg-white shadow"
                />
                {/* iOS pulsing glow: green while waiting, solid red once scanned */}
                <div
                  className={`absolute -inset-1 rounded-xl border-4 pointer-events-none ${
                    scanned ? 'border-red-500' : 'border-green-500 animate-pulse'
                  }`}
                />
                {scanned && <div className="absolute inset-0 bg-red-500/20 rounded-xl pointer-events-none" />}
              </div>
            ) : (
              <p className="text-sm font-medium text-red-500 h-[200px] flex items-center">
                {qrError || 'Failed to generate QR code'}
              </p>
            )}

            <p className="text-xs text-gray-400 text-center">Attach this receipt to the order bag</p>
          </div>

          {/* Order details */}
          <Card className="p-4 space-y-3">
            <p className="text-base font-bold text-gray-900">Order Details</p>
            <DetailRow label="Phone" value={order.customerPhone || 'Not provided'} />
            {order.customerAddress && <DetailRow label="Address" value={order.customerAddress} />}
            <DetailRow label="Items" value={`${order.orderItems.length} item(s)`} />
          </Card>

          {/* Instructions */}
          <Card className="p-4 space-y-3">
            <p className="text-base font-bold text-gray-900">Instructions</p>
            <InstructionRow number={1} text="Print this receipt" />
            <InstructionRow number={2} text="Attach receipt to order bag" />
            <InstructionRow number={3} text="Place bag in designated pickup area" />
            <InstructionRow number={4} text="Driver will scan QR code upon arrival" />
          </Card>

          <button
            onClick={() => printReceipt(order, qrDataUrl)}
            disabled={qrBusy && !qrDataUrl}
            className="w-full py-3 rounded-2xl bg-white border border-gray-200 text-gray-800 font-bold text-sm shadow-sm flex items-center justify-center gap-2 active:bg-gray-50 disabled:opacity-50"
          >
            <Printer size={16} className="text-[#FF6B35]" />
            Print Receipt
          </button>
        </div>
      )}
    </div>
  );
}
